import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Series from "../components/viewer/Series";
import ViewerCanvas from "../components/viewer/ViewerCanvas";
import ViewerToolbar from "../components/viewer/ViewerToolbar";
import AddTagsModal from "../components/viewer/AddTagsModal";
import CompareStudyModal from "../components/viewer/CompareStudyModal";
import ReportingTool from "../components/viewer/ReportingTool";
import DownloadZipPopup from "../components/viewer/DownloadZipPopup";
import JSZip from "jszip";
import "../styles/viewer.css";
import "../styles/dashboard.css";
const BASE_URL = process.env.REACT_APP_API_BASE_URL;
export default function Viewer() {
  const location = useLocation();
  const openReport = location.state?.openReport;
  const compareData = location.state?.compareData || [];
  
  const [zipStatus, setZipStatus] = useState("idle"); 
  const [zipUrl, setZipUrl] = useState(""); 
  const [showZipPopup, setShowZipPopup] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const [showReportingTool, setShowReportingTool] = useState(false);
  const [showSeries, setShowSeries] = useState(true);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [activeSeriesIndex, setActiveSeriesIndex] = useState(0);
  const [activeTool, setActiveTool] = useState("StackScrollTool");
  const [layout, setLayout] = useState({ rows: 1, cols: 1 });
  const [panelSeries, setPanelSeries] = useState({});
  const [activePanelIndex, setActivePanelIndex] = useState(0);
  const [allSeries, setAllSeries] = useState([]);
  const [mprView, setMprView] = useState(null);
  const [isSyncScroll, setIsSyncScroll] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const abortControllerRef = useRef(null);
  const locationPatient = location.state?.compareData || {};

  const selectedPatient = {
    ...locationPatient,
    patient_id: locationPatient.patient_id,
    patientName: locationPatient.patient_name,
    age: locationPatient.patient_age || "N/A",
    gender: locationPatient.patient_gender || "N/A",
    study: locationPatient.study || "N/A",
    studyDate: locationPatient?.studyDate,
    modality: selectedSeries?.modality || "N/A"
  };

  useEffect(() => {
    if (openReport) setShowReportingTool(true);
  }, [openReport]);

  const handleOpenReporting = () => setShowReportingTool(true);
  const handleCloseReporting = () => setShowReportingTool(false);

  const handleToolChange = (toolName) => {
    setActiveTool(toolName);
    if (toolName === "ReportingTool") handleOpenReporting();
  };

  const handleSelectSeries = (series, index) => {
    setSelectedSeries(series);
    setActiveSeriesIndex(index);
    setPanelSeries((prev) => {
      if (mprView === "THREE_VIEW") {
        return {
          0: { series, index },
          1: { series, index },
          2: { series, index },
        };
      }
      return {
        ...prev,
        [activePanelIndex]: { series, index },
      };
    });
  };

  const handleMPRViewChange = (view) => {
    console.log("Changing MPR:", view);
    setMprView(view);
    if (view === "THREE_VIEW") {
      setLayout({ rows: 1, cols: 3 });
      setPanelSeries({
        0: { series: selectedSeries, index: activeSeriesIndex },
        1: { series: selectedSeries, index: activeSeriesIndex },
        2: { series: selectedSeries, index: activeSeriesIndex },
      });
    } else {
      setLayout({ rows: 1, cols: 1 });
      setPanelSeries({
        0: { series: selectedSeries, index: activeSeriesIndex },
      });
      console.log(panelSeries,'panelSeries')
    }
  };
  
  const handleDownloadZip = async (series) => {
    if (!series?.length) {
      alert("No images available.");
      return;
    }
  
    if (isDownloadingZip) return;

    abortControllerRef.current = new AbortController();
  
    try {
      setIsDownloadingZip(true);
      const zip = new JSZip();
      const validSeries = series.filter((seq) => seq.modality !== "SR");
  
      let totalImages = 0;
      let downloadedImages = 0;
  
      validSeries.forEach((seq) => {
        totalImages += seq.imageIds?.length || 0;
      });
  
      setDownloadProgress({ current: 0, total: totalImages });

      // Flatten list of tasks to execute with concurrency control
      const tasks = [];
      validSeries.forEach((seq) => {
        const imageIds = seq.imageIds || [];
        imageIds.forEach((imgId, i) => {
          tasks.push({ seq, imgId, index: i });
        });
      });

      // Helper chunking function to prevent browser network starvation (max 5 parallel requests)
      const concurrencyLimit = 5;
      for (let i = 0; i < tasks.length; i += concurrencyLimit) {
        if (abortControllerRef.current?.signal.aborted) {
          throw new Error("DownloadCancelled");
        }

        const chunk = tasks.slice(i, i + concurrencyLimit);
        await Promise.all(
          chunk.map(async ({ seq, imgId, index }) => {
            if (abortControllerRef.current?.signal.aborted) return;

            let imageUrl = imgId;
            if (imageUrl.startsWith("wadouri:")) {
              imageUrl = imageUrl.replace("wadouri:", "");
            }
            if (imageUrl.startsWith("/") && BASE_URL) {
              imageUrl = `${BASE_URL}${imageUrl}`;
            }

            try {
              const response = await fetch(imageUrl, {
                signal: abortControllerRef.current.signal,
              });

              if (!response.ok) return;

              const blob = await response.blob();
              const filename =
                imageUrl.split("/").pop()?.split("?")[0] || `${index + 1}.dcm`;
              const safeFilename = filename.endsWith(".dcm") ? filename : `${filename}.dcm`;

              zip.file(`${seq.seriesName || seq.id}/${safeFilename}`, blob);

              downloadedImages++;
              setDownloadProgress({ current: downloadedImages, total: totalImages });
            } catch (err) {
              if (err.name === "AbortError") return;
              console.warn("Failed fetching image:", imageUrl);
            }
          })
        );
      }
  
      if (downloadedImages === 0) {
        alert("No images could be downloaded.");
        return;
      }
  
      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });
  
      if (abortControllerRef.current?.signal.aborted) return;
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `study_${selectedPatient.patient_name}_${selectedPatient.study_description}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
  
    } catch (error) {
      if (error.message !== "DownloadCancelled") {
        console.error("ZIP generation failed:", error);
        alert("Failed to create ZIP.");
      }
    } finally {
      setIsDownloadingZip(false);
      setDownloadProgress({ current: 0, total: 0 });
      abortControllerRef.current = null;
    }
  };

  const handleCancelDownload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsDownloadingZip(false);
    setDownloadProgress({ current: 0, total: 0 });
  };

  // const handleDownloadZip = async () => {
  //   const studyId = selectedPatient?.studyId || selectedPatient?.id || selectedSeries?.studyId;
  //   if (!studyId) {
  //     alert("Study ID not available.");
  //     return;
  //   }

  //   setShowZipPopup(true);
  //   setZipStatus("processing");
  //   setZipUrl("");

  //   let intervalId;
  //   const checkStatus = async () => {
  //     try {
  //       const data = await getSharingLinkFromServer(studyId);
  //       if (data.status === "ready") {
  //         clearInterval(intervalId);
  //         setZipStatus("success");
  //         setZipUrl(data.zip_url);
  //       } else if (data.status !== "processing") {
  //         clearInterval(intervalId);
  //         setZipStatus("error");
  //       }
  //     } catch (err) {
  //       clearInterval(intervalId);
  //       setZipStatus("error");
  //     }
  //   };
  //   await checkStatus();
  //   intervalId = setInterval(checkStatus, 3000);
  // };

  const handleLayoutChange = (rows, cols) => {
    const totalPanels = rows * cols;
    const newPanelSeries = {};
    for (let i = 0; i < totalPanels; i++) {
      // Every panel starts out showing the series the user currently has
      // selected, not whatever happens to sit at index i in allSeries.
      // The user can still drag a different series onto any panel afterward.
      newPanelSeries[i] = { series: selectedSeries, index: activeSeriesIndex };
    }
    setLayout({ rows, cols });
    setPanelSeries(newPanelSeries);
    setActivePanelIndex(0);
    setMprView(null);
  };

  const toggleSeriesPanel = () => setShowSeries((prev) => !prev);

  return (
    <>
      <div className="viewerPage">
        <ViewerToolbar
          onToolChange={handleToolChange}
          currentPatient={selectedPatient}
          currentActiveTool={activeTool}
          setActiveTool={setActiveTool}
          onMPRViewChange={handleMPRViewChange}
          onToggleSeries={toggleSeriesPanel}
          onSyncToggle={() => setIsSyncScroll(!isSyncScroll)}
          isSyncScroll={isSyncScroll}
          onAddTags={() => setShowTagModal(true)}
          onCompareStudy={() => setShowCompareModal(true)}
          onDownloadZip={()=>handleDownloadZip(allSeries)}
          onLayoutChange={handleLayoutChange}
          handleOpenReporting={handleOpenReporting}
        />
        <div className="viewerLayout">
          <div className="viewerContainer">
            {showSeries && (
              <div className="seriesPanel">
                <Series onSelectSeries={handleSelectSeries} onSeriesLoad={setAllSeries} />
              </div>
            )}
            {showTagModal && <AddTagsModal onClose={() => setShowTagModal(false)} />}
            {showCompareModal && <CompareStudyModal data={compareData} onClose={() => setShowCompareModal(false)} />}
            {showReportingTool && (
              <ReportingTool
                viewportId={activePanelIndex.toString()}
                selectedSeries={selectedSeries}
                patientData={compareData}
                onClose={handleCloseReporting}
              />
            )}
            <div className="canvasPanel">
              <ViewerCanvas
                selectedSeries={selectedSeries}
                seriesIndex={activeSeriesIndex}
                onBack={toggleSeriesPanel}
                mprView={mprView}
                isSyncScroll={isSyncScroll}
                activeTool={activeTool}
                layout={layout}
                panelSeries={panelSeries}
                activePanelIndex={activePanelIndex}
                onPanelClick={setActivePanelIndex}
                setMprView={setMprView}
              />
            </div>
          </div>
        </div>
      </div>
      {isDownloadingZip && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, width: "100vw", height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex", justifyContent: "center", alignItems: "center",
          zIndex: 9999, color: "#fff", flexDirection: "column", fontFamily: "sans-serif"
        }}>
          <div style={{
            background: "#222", padding: "30px", borderRadius: "8px", textAlign: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)", width: "320px", position: "relative"
          }}>
            <h3 style={{ margin: "0 0 10px 0" }}>Preparing Study ZIP...</h3>
            <p style={{ margin: "10px 0", fontSize: "14px", color: "#ccc" }}>
              Downloading image {downloadProgress.current} of {downloadProgress.total}
            </p>
            <div style={{ width: "100%", background: "#444", height: "8px", borderRadius: "4px", overflow: "hidden", margin: "15px 0" }}>
              <div style={{
                width: `${downloadProgress.total ? (downloadProgress.current / downloadProgress.total) * 100 : 0}%`,
                background: "#3b82f6", height: "100%", transition: "width 0.1s ease"
              }} />
            </div>
            
            <button
              onClick={handleCancelDownload}
              style={{
                marginTop: "10px",
                padding: "8px 16px",
                background: "#ef4444",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "bold",
                width: "100%"
              }}
            >
              Cancel / Close
            </button>
          </div>
        </div>
      )}
      {showZipPopup && (
        <DownloadZipPopup
          status={zipStatus}
          zipUrl={zipUrl}
          onClose={() => setShowZipPopup(false)}
        />
      )}
    </>
  );
}